import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { PremiumBadge, LevelBadge, VerifiedBadge, ReliabilityRow } from "@/components/Badges";
import SmartImage from "@/components/SmartImage";
import Masonry, { MasonryItem } from "@/components/Masonry";
import {
  ArrowLeft, MapPin, Share2, MessageCircle, Sparkles, Quote,
  Palette, Plane, CheckCircle2, Camera, Briefcase,
  CircleDot, Globe2, Compass, Star,
} from "lucide-react";
import { toast } from "sonner";
import { getTemplate, templateStyle, DEFAULT_TEMPLATE } from "@/lib/portfolioTemplates";

const CATEGORIES = [
  "Shooting", "Beauté", "Éditorial", "Streetwear",
  "Makeup", "Runway", "Collaboration", "Projet personnel",
];

function Chip({ children, variant = "default" }) {
  const styles = {
    default: "bg-[#FAF3EA] text-[#1A1A1A] border-[#EADDC9]",
    gold: "bg-[#FAF3EA] text-[#8B5A2B] border-[#E6C29A]",
    outline: "bg-white text-[#1A1A1A] border-[#D4C3A5]",
    dark: "bg-[#1A1A1A] text-white border-[#1A1A1A]",
  };
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs border ${styles[variant]}`}>
      {children}
    </span>
  );
}

function SectionHeader({ eyebrow, title, subtitle }) {
  return (
    <div className="mb-8">
      {eyebrow && <p className="caption-label" style={{ color: "var(--pt-accent, #C68E59)" }}>{eyebrow}</p>}
      <h2 className="text-3xl sm:text-4xl mt-1 leading-tight" style={{ fontFamily: "var(--pt-font-title)", color: "var(--pt-fg, #1A1A1A)" }}>{title}</h2>
      {subtitle && <p className="mt-2 text-sm max-w-xl" style={{ color: "var(--pt-muted, #666)" }}>{subtitle}</p>}
    </div>
  );
}

function AvailabilityBadge({ active, icon: Icon, label }) {
  return (
    <div
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border text-sm transition ${
        active
          ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
          : "bg-[#FAF3EA] text-[#8B8680] border-[#E5E5E5]"
      }`}
    >
      <Icon className={`w-3.5 h-3.5 ${active ? "text-[#C68E59]" : ""}`} />
      <span>{label}</span>
    </div>
  );
}

/**
 * Editorial mosaic: alternates between a "feature" full-width row and a 2/3-col grid.
 * Pattern: [feature 1] · [grid: 2-3 photos] · [feature 1] · [grid: 2-3 photos]…
 * Generates a magazine-spread feeling instead of a uniform grid.
 */
function EditorialMosaic({ photos }) {
  const blocks = [];
  let i = 0;
  let blockIndex = 0;
  while (i < photos.length) {
    const isFeatureRow = blockIndex % 2 === 0;
    if (isFeatureRow) {
      // Single hero photo full-width on this row
      const photo = photos[i];
      blocks.push(
        <div key={photo.id} className="relative group rounded-2xl overflow-hidden" data-testid={`mosaic-feature-${photo.id}`}>
          <SmartImage
            photo={photo}
            aspect="aspect-[16/9]"
            imgClassName="group-hover:scale-[1.02] transition duration-700"
          />
          {(photo.title || photo.category) && (
            <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-black/75 via-black/30 to-transparent text-white pointer-events-none">
              {photo.category && <p className="caption-label text-[#E6C29A]">{photo.category}</p>}
              {photo.title && <p className="font-serif text-xl mt-1">{photo.title}</p>}
            </div>
          )}
        </div>
      );
      i += 1;
    } else {
      // Pull next 2 or 3 photos for a grid
      const take = i + 3 <= photos.length ? 3 : (photos.length - i);
      const slice = photos.slice(i, i + take);
      blocks.push(
        <div key={`grid-${i}`} className={`grid gap-4 ${take === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-2 sm:grid-cols-3"}`}>
          {slice.map((p, idx) => (
            <div key={p.id} className="relative group rounded-2xl overflow-hidden" data-testid={`mosaic-tile-${p.id}`}>
              <SmartImage
                photo={p}
                aspect={idx === 0 ? "aspect-[3/4]" : idx === 1 ? "aspect-square" : "aspect-[4/5]"}
                imgClassName="group-hover:scale-105 transition duration-500"
              />
              {(p.title || p.category) && (
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/65 to-transparent opacity-0 group-hover:opacity-100 transition text-white pointer-events-none">
                  {p.category && <p className="text-[10px] opacity-80 tracking-widest uppercase">{p.category}</p>}
                  {p.title && <p className="text-xs font-medium mt-0.5">{p.title}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      );
      i += take;
    }
    blockIndex += 1;
  }
  return <div className="space-y-4 sm:space-y-6">{blocks}</div>;
}

/** Pinterest-style masonry where photos keep their natural aspect ratio. */
function NaturalMasonry({ photos }) {
  return (
    <Masonry cols={{ base: 2, sm: 2, lg: 3 }} gap="gap-4">
      {photos.map((p) => (
        <MasonryItem key={p.id}>
          <div className="relative group rounded-2xl overflow-hidden" data-testid={`masonry-tile-${p.id}`}>
            <SmartImage
              photo={p}
              aspect={null}
              imgClassName="group-hover:scale-[1.02] transition duration-500"
            />
            {(p.title || p.category) && (
              <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition text-white pointer-events-none">
                {p.category && <p className="text-[10px] opacity-80 tracking-widest uppercase">{p.category}</p>}
                {p.title && <p className="text-xs font-medium mt-0.5">{p.title}</p>}
              </div>
            )}
          </div>
        </MasonryItem>
      ))}
    </Masonry>
  );
}

/** Uniform aspect-ratio grid, used by minimal_clean (4/5) and artistic (1/1). */
function UniformGrid({ photos, aspect }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
      {photos.map((p) => (
        <div key={p.id} className="relative group rounded-2xl overflow-hidden" data-testid={`grid-tile-${p.id}`}>
          <SmartImage
            photo={p}
            aspect={aspect}
            imgClassName="group-hover:scale-105 transition duration-500"
          />
          {(p.title || p.category) && (
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition text-white pointer-events-none">
              {p.category && <p className="text-[10px] opacity-80 tracking-widest uppercase">{p.category}</p>}
              {p.title && <p className="text-xs font-medium mt-0.5">{p.title}</p>}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Picks the right gallery component based on the template's gallery.layout. */
function GalleryByLayout({ photos, layout, aspect }) {
  if (layout === "masonry") return <NaturalMasonry photos={photos} />;
  if (layout === "grid") return <UniformGrid photos={photos} aspect={aspect} />;
  return <EditorialMosaic photos={photos} />;
}

export default function TalentProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [activeCat, setActiveCat] = useState("Tous");

  useEffect(() => {
    setError(false);
    api.get(`/talents/${id}`)
      .then(({ data }) => {
        // normalize so downstream `.find/.filter/.map` never crash
        setData({
          ...data,
          photos: Array.isArray(data?.photos) ? data.photos : [],
          projects: Array.isArray(data?.projects) ? data.projects : [],
        });
      })
      .catch(() => setError(true));
  }, [id]);

  const mainPhoto = useMemo(() => {
    if (!data) return null;
    return data.photos.find((p) => p.is_main) || data.photos[0] || null;
  }, [data]);

  const featured = useMemo(() => {
    if (!data) return [];
    return data.photos.filter((p) => p.featured && !p.is_main).slice(0, 4);
  }, [data]);

  const galleryByCat = useMemo(() => {
    if (!data) return [];
    const pool = data.photos.filter((p) => p.id !== mainPhoto?.id);
    if (activeCat === "Tous") return pool;
    return pool.filter((p) => p.category === activeCat);
  }, [data, mainPhoto, activeCat]);

  const availableCats = useMemo(() => {
    if (!data) return [];
    const set = new Set(data.photos.map((p) => p.category).filter(Boolean));
    return CATEGORIES.filter((c) => set.has(c));
  }, [data]);

  if (error) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <p className="caption-label text-[#A26F3E]">Talent introuvable</p>
          <h1 className="font-serif text-4xl text-[#1A1A1A] mt-2">Ce profil n'est pas disponible</h1>
          <p className="text-[#666] mt-3">Il a peut-être été retiré ou le lien est incorrect.</p>
          <Button onClick={() => navigate("/createurs/talents")} className="mt-8 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white">
            Voir tous les talents
          </Button>
        </div>
      </AppShell>
    );
  }

  if (!data) {
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-6 py-24 text-center text-[#8B8680]">Chargement…</div>
      </AppShell>
    );
  }

  const { profile, user, photos, portfolio_profile: pp, projects } = data;
  const mainSrc = mainPhoto ? `${API}/files/${mainPhoto.storage_path}` : profile.demo_photo_url;
  const displayName = user?.name;
  const templateId = pp?.template_id || DEFAULT_TEMPLATE;
  const tpl = getTemplate(templateId);
  const tplStyle = templateStyle(templateId);
  const hasUniverse = (pp?.mood_tags?.length || 0) + (pp?.inspirations?.length || 0) + (pp?.favorite_styles?.length || 0) + (pp?.artistic_references?.length || 0) + (pp?.favorite_colors?.length || 0) > 0;
  const hasAvailability = pp && (pp.preferred_locations?.length > 0 || pp.collaboration_types?.length > 0 || pp.dream_projects);
  const hasSignature = pp && (pp.personal_statement || pp.main_goal || pp.main_inspiration);

  const share = async () => {
    const url = `${window.location.origin}/talents/${id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: `${displayName} · Helencia`, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Lien copié dans le presse-papiers");
      }
    } catch {
      /* user cancelled */
    }
  };

  const startCollab = async () => {
    // Same fix as the editorial profile: check if the brand already has active
    // collabs before nagging them to publish a new one. Legacy classic view —
    // simplified: no dialog, just skip the toast when they already have one.
    try {
      const { data } = await api.get("/my/collaborations");
      const active = Array.isArray(data) && data.some((c) => c.status === "active" || c.status === "open");
      if (active) {
        navigate("/createurs/collaborations");
        return;
      }
    } catch { /* not creator / not logged in — fall through */ }
    navigate("/createurs/collaborations");
    toast("Publiez une collaboration pour contacter ce talent.");
  };

  return (
    <AppShell>
      <div data-testid="talent-profile-page" data-template={templateId} className="min-h-screen pt-themed" style={{ ...tplStyle, background: "var(--pt-bg)", color: "var(--pt-fg)" }}>
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-10 pb-24">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm hover:opacity-80 mb-8" style={{ color: "var(--pt-muted)" }}>
            <ArrowLeft className="w-4 h-4" /> Retour
          </button>

          {/* =================== HERO (cinematic editorial) =================== */}
          <section className="relative" data-testid="hero">
            {(mainPhoto || mainSrc) ? (
              <div className="relative rounded-[28px] overflow-hidden bg-[#1A1A1A] aspect-[16/10] sm:aspect-[16/8] shadow-[0_30px_80px_-30px_rgba(0,0,0,0.35)]">
                {mainPhoto ? (
                  <img
                    src={`${API}/files/${mainPhoto.storage_path}`}
                    alt={displayName}
                    width={mainPhoto.width || undefined}
                    height={mainPhoto.height || undefined}
                    decoding="async"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `${((mainPhoto.focal_x ?? 0.5) * 100).toFixed(2)}% ${((mainPhoto.focal_y ?? 0.5) * 100).toFixed(2)}%` }}
                  />
                ) : (
                  <img src={mainSrc} alt={displayName} className="w-full h-full object-cover" />
                )}
                {/* Editorial top strip: small badges */}
                <div className="absolute top-5 left-5 flex flex-wrap gap-2">
                  <LevelBadge level={profile.level} />
                  {profile.is_premium && <PremiumBadge />}
                </div>
                {pp?.availability_status && (
                  <div className="absolute top-5 right-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/90 backdrop-blur text-xs">
                    <span className="w-2 h-2 rounded-full bg-[#2E7D32] animate-pulse" />
                    Disponible pour des projets
                  </div>
                )}

                {/* Bottom gradient overlay with name + meta */}
                <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-14 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white">
                  <p className="caption-label" style={{ color: "var(--pt-accent)" }}>Portfolio Helencia</p>
                  <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl mt-2 leading-[0.95] tracking-tight" style={{ fontFamily: "var(--pt-font-title)" }} data-testid="hero-name">
                    {displayName}
                  </h1>
                  {(pp?.headline || profile.talent_type) && (
                    <p className="mt-4 text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl">
                      {pp?.headline || profile.talent_type}
                    </p>
                  )}
                  <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
                    {profile.location && (
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="w-4 h-4" /> {profile.location}
                      </span>
                    )}
                    {profile.experience && (
                      <span className="inline-flex items-center gap-1.5">
                        <Briefcase className="w-4 h-4" /> {profile.experience}
                      </span>
                    )}
                    {profile.talent_type && pp?.headline && (
                      <span className="inline-flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4" /> {profile.talent_type}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-[28px] aspect-[16/9] bg-[#FAF3EA]" />
            )}

            {/* Action row + reliability — magazine sub-deck */}
            <div className="mt-8 flex flex-col lg:flex-row lg:items-center justify-between gap-6">
              <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
                {profile.badges?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {profile.badges.slice(0, 6).map((b) => (
                      <Chip key={b} variant="gold">{b}</Chip>
                    ))}
                  </div>
                )}
                {data.reliability && (data.reliability.review_count > 0 || data.reliability.badges?.length > 0) && (
                  <div data-testid="profile-reliability">
                    <ReliabilityRow
                      rating={data.reliability.avg_rating || 0}
                      count={data.reliability.review_count || 0}
                      badges={data.reliability.badges || []}
                      max={4}
                    />
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={startCollab}
                  className="rounded-full text-white px-6 h-11 transition-opacity hover:opacity-90"
                  style={{ background: "var(--pt-accent)" }}
                  data-testid="collab-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" /> Collaborer
                </Button>
                <Button
                  variant="outline"
                  onClick={share}
                  className="rounded-full px-6 h-11"
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Partager le profil
                </Button>
              </div>
            </div>
          </section>

          {/* =================== SIGNATURE PHRASE (visuel fort) =================== */}
          {pp?.signature_quote && (
            <section className="mt-20 sm:mt-24" data-testid="signature-quote">
              <div className="relative max-w-4xl mx-auto text-center px-4">
                <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[120px] sm:text-[180px] leading-none select-none pointer-events-none" style={{ fontFamily: "var(--pt-font-title)", color: "var(--pt-accent)", opacity: 0.18 }}>"</span>
                <p className="italic text-3xl sm:text-5xl lg:text-6xl leading-[1.15] relative" style={{ fontFamily: "var(--pt-font-title)", color: "var(--pt-fg)" }}>
                  {pp.signature_quote}
                </p>
                <div className="mt-6 inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em]" style={{ color: "var(--pt-accent)" }}>
                  <span className="w-8 h-px" style={{ background: "var(--pt-accent)" }} />
                  Signature {displayName?.split(" ")[0]}
                  <span className="w-8 h-px" style={{ background: "var(--pt-accent)" }} />
                </div>
              </div>
            </section>
          )}

          {/* =================== MON HISTOIRE =================== */}
          {(pp?.bio || profile.bio || pp?.dream_projects) && (
            <section className="mt-24" data-testid="story-section">
              <SectionHeader eyebrow="Mon histoire" title="L'univers, le parcours, l'envie" />
              <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start">
                <div className="lg:col-span-7">
                  {(pp?.bio || profile.bio) && (
                    <p className="text-2xl sm:text-3xl leading-[1.3] tracking-[-0.01em] portfolio-dropcap" style={{ fontFamily: "var(--pt-font-title)", color: "var(--pt-fg)" }}>
                      {pp?.bio || profile.bio}
                    </p>
                  )}
                </div>
                <aside className="lg:col-span-5 space-y-6">
                  {pp?.dream_projects && (
                    <div className="rounded-3xl bg-[#1A1A1A] text-white p-7">
                      <p className="caption-label text-[#E6C29A]">Projets rêvés</p>
                      <p className="font-serif text-xl mt-3 leading-relaxed">{pp.dream_projects}</p>
                    </div>
                  )}
                  {pp?.creative_vision && (
                    <div className="rounded-3xl bg-white border border-[#EADDC9] p-6">
                      <p className="caption-label text-[#C68E59]">Vision créative</p>
                      <p className="text-[#1A1A1A] mt-2 leading-relaxed">{pp.creative_vision}</p>
                    </div>
                  )}
                </aside>
              </div>
            </section>
          )}

          {/* =================== UNIVERS =================== */}
          {hasUniverse && (
            <section className="mt-24">
              <SectionHeader eyebrow="Mon univers" title="Esthétique & inspirations" subtitle="Ce qui nourrit mes projets et mon regard sur la mode." />

              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {pp.mood_tags?.length > 0 && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7">
                    <div className="flex items-center gap-2 mb-4">
                      <Sparkles className="w-4 h-4 text-[#C68E59]" />
                      <h3 className="font-serif text-lg text-[#1A1A1A]">Personnalité</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pp.mood_tags.map((t) => <Chip key={t} variant="dark">{t}</Chip>)}
                    </div>
                  </div>
                )}

                {pp.favorite_styles?.length > 0 && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7">
                    <div className="flex items-center gap-2 mb-4">
                      <Palette className="w-4 h-4 text-[#C68E59]" />
                      <h3 className="font-serif text-lg text-[#1A1A1A]">Styles favoris</h3>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {pp.favorite_styles.map((t) => <Chip key={t}>{t}</Chip>)}
                    </div>
                  </div>
                )}

                {pp.inspirations?.length > 0 && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7">
                    <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Inspirations</h3>
                    <div className="flex flex-wrap gap-2">
                      {pp.inspirations.map((t) => <Chip key={t} variant="outline">{t}</Chip>)}
                    </div>
                  </div>
                )}

                {pp.favorite_colors?.length > 0 && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7">
                    <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Palette</h3>
                    <div className="flex flex-wrap gap-2">
                      {pp.favorite_colors.map((t) => <Chip key={t}>{t}</Chip>)}
                    </div>
                  </div>
                )}

                {pp.artistic_references?.length > 0 && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7 md:col-span-2">
                    <h3 className="font-serif text-lg text-[#1A1A1A] mb-4">Références artistiques</h3>
                    <div className="flex flex-wrap gap-2">
                      {pp.artistic_references.map((t) => <Chip key={t} variant="outline">{t}</Chip>)}
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* =================== FEATURED =================== */}
          {featured.length > 0 && (
            <section className="mt-24">
              <SectionHeader eyebrow="À la une" title="Pièces maîtresses" />
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {featured.map((p) => (
                  <div key={p.id} className="relative rounded-2xl overflow-hidden group">
                    <SmartImage
                      photo={p}
                      aspect="aspect-[3/4]"
                      imgClassName="group-hover:scale-105 transition duration-500"
                    />
                    {p.title && (
                      <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/70 to-transparent text-white text-xs pointer-events-none">
                        <p className="font-medium">{p.title}</p>
                        {p.category && <p className="opacity-70 tracking-widest uppercase text-[10px] mt-0.5">{p.category}</p>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* =================== GALLERY =================== */}
          {photos.length > 1 && (
            <section className="mt-24">
              <SectionHeader eyebrow="Galerie" title="Portfolio photo" />

              {availableCats.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-6">
                  <button
                    onClick={() => setActiveCat("Tous")}
                    className={`px-4 py-1.5 rounded-full text-xs border transition ${activeCat === "Tous" ? "text-white" : "bg-white text-[#1A1A1A] border-[#D4C3A5] hover:bg-[#FAF3EA]"}`}
                    style={activeCat === "Tous" ? { background: "var(--pt-accent)", borderColor: "var(--pt-accent)" } : undefined}
                  >
                    Tous
                  </button>
                  {availableCats.map((c) => (
                    <button
                      key={c}
                      onClick={() => setActiveCat(c)}
                      className={`px-4 py-1.5 rounded-full text-xs border transition ${activeCat === c ? "text-white" : "bg-white text-[#1A1A1A] border-[#D4C3A5] hover:bg-[#FAF3EA]"}`}
                      style={activeCat === c ? { background: "var(--pt-accent)", borderColor: "var(--pt-accent)" } : undefined}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              )}

              {galleryByCat.length === 0 ? (
                <div className="rounded-3xl bg-[#FAF3EA] p-12 text-center text-sm text-[#8B8680]">
                  <Camera className="w-8 h-8 text-[#B3A78C] mx-auto mb-3" />
                  Aucune photo dans cette catégorie
                </div>
              ) : (
                <GalleryByLayout
                  photos={galleryByCat}
                  layout={tpl.gallery?.layout || "mosaic"}
                  aspect={tpl.gallery?.aspect || "aspect-[4/5]"}
                />
              )}
            </section>
          )}

          {/* =================== PROJECTS =================== */}
          {projects?.length > 0 && (
            <section className="mt-24">
              <SectionHeader eyebrow="Expériences" title="Projets & collaborations" />
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                {projects.map((p) => (
                  <div key={p.id} className="rounded-3xl overflow-hidden bg-white border border-[#EADDC9] group">
                    <div className="aspect-[4/3] bg-[#FAF3EA]">
                      {p.storage_path ? (
                        <img
                          src={`${API}/files/${p.storage_path}`}
                          alt={p.title}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[11px] tracking-widest uppercase text-[#B3A78C]">Projet</div>
                      )}
                    </div>
                    <div className="p-6">
                      <div className="flex items-center gap-2 text-[11px] tracking-widest uppercase text-[#8B8680] mb-2">
                        {p.role && <span>{p.role}</span>}
                        {p.date && <span>· {p.date}</span>}
                      </div>
                      <h4 className="font-serif text-xl text-[#1A1A1A] leading-snug">{p.title}</h4>
                      {p.description && <p className="text-sm text-[#555] mt-2 leading-relaxed">{p.description}</p>}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* =================== AVAILABILITY (badges éditoriales) =================== */}
          {hasAvailability && (
            <section className="mt-24" data-testid="availability">
              <SectionHeader eyebrow="Disponibilités" title="Envies & terrains de jeu" />

              {/* Top badges row */}
              <div className="flex flex-wrap gap-3 mb-8">
                <AvailabilityBadge
                  active={pp.availability_status}
                  icon={CircleDot}
                  label={pp.availability_status ? "Disponible" : "Indisponible"}
                />
                {pp.willing_to_travel && (
                  <AvailabilityBadge active icon={Plane} label="Prêt à voyager" />
                )}
                {profile.location && (
                  <AvailabilityBadge active icon={MapPin} label={`Basé(e) à ${profile.location}`} />
                )}
              </div>

              <div className="rounded-3xl bg-gradient-to-br from-[#FAF3EA] to-white border border-[#E6C29A] p-8 lg:p-10">
                <div className="grid gap-8 md:grid-cols-2">
                  {pp.preferred_locations?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-[#8B7320] mb-4">
                        <Globe2 className="w-4 h-4" /> Zones géographiques
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {pp.preferred_locations.map((c) => <Chip key={c} variant="outline">{c}</Chip>)}
                      </div>
                    </div>
                  )}

                  {pp.collaboration_types?.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 text-xs tracking-widest uppercase text-[#8B7320] mb-4">
                        <Compass className="w-4 h-4" /> Types de projets
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {pp.collaboration_types.map((c) => <Chip key={c} variant="outline">{c}</Chip>)}
                      </div>
                    </div>
                  )}
                </div>

                {pp.dream_projects && (
                  <div className="mt-8 pt-8 border-t border-[#E6C29A]">
                    <p className="caption-label text-[#8B7320]">Projets rêvés</p>
                    <p className="font-serif text-lg text-[#1A1A1A] mt-2 leading-relaxed">{pp.dream_projects}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* =================== SIGNATURE (Ce qui me représente) =================== */}
          {hasSignature && (
            <section className="mt-24" data-testid="vision-section">
              <SectionHeader eyebrow="Ce qui me représente" title="Vision & intention" />
              <div className="grid gap-6 md:grid-cols-2">
                {pp.personal_statement && (
                  <div className="rounded-3xl bg-[#1A1A1A] text-white p-8 md:col-span-2 relative overflow-hidden">
                    <span className="absolute -top-6 -left-2 text-[180px] leading-none font-serif text-[#C68E59]/20 select-none pointer-events-none">"</span>
                    <Quote className="w-6 h-6 text-[#C68E59] mb-4 relative" />
                    <p className="font-serif text-2xl lg:text-3xl leading-snug relative">
                      {pp.personal_statement}
                    </p>
                  </div>
                )}
                {pp.main_goal && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7">
                    <div className="flex items-center gap-2 mb-2">
                      <Star className="w-4 h-4 text-[#C68E59]" />
                      <p className="caption-label text-[#C68E59]">Objectif</p>
                    </div>
                    <p className="text-[#1A1A1A] mt-2 leading-relaxed">{pp.main_goal}</p>
                  </div>
                )}
                {pp.main_inspiration && (
                  <div className="rounded-3xl bg-white border border-[#EADDC9] p-7">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="w-4 h-4 text-[#C68E59]" />
                      <p className="caption-label text-[#C68E59]">Inspiration principale</p>
                    </div>
                    <p className="text-[#1A1A1A] mt-2 leading-relaxed">{pp.main_inspiration}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* =================== FINAL CTA =================== */}
          <section className="mt-24">
            <div className="rounded-3xl text-white p-10 lg:p-14 flex flex-col lg:flex-row items-center justify-between gap-8" style={{ background: "var(--pt-quote-bg, #1A1A1A)", color: "var(--pt-quote-fg, #FFFFFF)" }}>
              <div className="max-w-xl">
                <p className="caption-label" style={{ color: "var(--pt-accent)" }}>Créons ensemble</p>
                <h3 className="text-3xl lg:text-4xl mt-2 leading-tight" style={{ fontFamily: "var(--pt-font-title)" }}>
                  Envie de travailler avec {displayName?.split(" ")[0] || "ce talent"} ?
                </h3>
                <p className="mt-3 opacity-75">Lancez une collaboration Helencia pour initier le contact.</p>
              </div>
              <div className="flex gap-3">
                <Button onClick={startCollab} className="rounded-full bg-white text-[#1A1A1A] hover:bg-white/90 px-6 h-12" data-testid="final-collab-btn">
                  Proposer un projet
                </Button>
                <Button onClick={share} variant="outline" className="rounded-full border-white/40 text-white hover:bg-white/10 px-6 h-12 bg-transparent">
                  <Share2 className="w-4 h-4 mr-2" /> Partager
                </Button>
              </div>
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  );
}
